import { useCallback, useEffect, useRef, useState } from 'react';
import { storage } from '../../utils/platform';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import { API_BASE } from '../../config/api';

const SYNC_TTL_MS = 60 * 60 * 1000; // 1h
const LAST_SYNC_KEY = 'revyo_last_sync';

function isMissingTableError(payload, status) {
  // Le backend renvoie 500 + 'Erreur serveur' générique. On ne peut pas
  // distinguer "table absente" d'une autre erreur SQL via le payload.
  // Stratégie : tout 500 sur /review-replies déclenche le fallback.
  return status === 500;
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  let payload = null;
  try { payload = await res.json(); } catch (_) {}
  if (!res.ok) {
    const err = new Error(payload?.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return payload;
}

export function useReviews() {
  // Vague 10 : refetch automatique au switch de restaurant.
  const { currentRestaurantId } = useCurrentRestaurant();
  const [reviews, setReviews] = useState([]);
  const [totalGlobal, setTotalGlobal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [lastSyncAt, setLastSyncAt] = useState(null);
  const [fallbackMode, setFallbackMode] = useState(false);
  const cancelRef = useRef(false);

  const getHeaders = useCallback(async () => {
    const token = await storage.get('token');
    const base = token
      ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      : { 'Content-Type': 'application/json' };
    return currentRestaurantId
      ? { ...base, 'X-Restaurant-Id': currentRestaurantId }
      : base;
  }, [currentRestaurantId]);

  const fetchTotalGlobal = useCallback(async () => {
    try {
      const headers = await getHeaders();
      const data = await fetchJson(`${API_BASE}/api/dashboard/google-reviews?limit=1`, { headers });
      return data?.total ?? 0;
    } catch (e) {
      console.warn('[useReviews] fetchTotalGlobal failed:', e.message);
      return 0;
    }
  }, [getHeaders]);

  // Fallback : récupère les avis depuis /google-reviews (pas de statut réponse)
  const fetchReviewsFallback = useCallback(async (headers) => {
    // TODO Session 4+ : pagination — le LIMIT 50 hardcodé côté backend nous
    // limite à 50 avis affichables ici, idem pour /review-replies. Acceptable
    // en V1, à revoir quand on aura besoin de scroll infinite.
    const data = await fetchJson(
      `${API_BASE}/api/dashboard/google-reviews?limit=50&sort=newest`,
      { headers }
    );
    const list = Array.isArray(data?.reviews) ? data.reviews : [];
    return list.map((r) => ({
      review_id: null, // pas d'id côté google-reviews → actions impossibles
      author_name: r.author_name,
      rating: r.rating,
      text: r.text,
      review_time: r.review_time,
      profile_photo_url: r.profile_photo_url || null,
      reply_id: null,
      reply_text: null,
      reply_status: null, // statut inconnu en fallback
    }));
  }, []);

  const fetchReviews = useCallback(async () => {
    setError(null);
    const headers = await getHeaders();
    try {
      const data = await fetchJson(`${API_BASE}/api/dashboard/review-replies`, { headers });
      const list = Array.isArray(data?.reviews) ? data.reviews : [];
      setFallbackMode(false);
      return list;
    } catch (e) {
      // Si la table review_replies n'existe pas en prod (dette technique),
      // on bascule sur /google-reviews en mode lecture seule.
      if (isMissingTableError(e.payload, e.status)) {
        console.warn('[useReviews] /review-replies KO → fallback /google-reviews');
        const list = await fetchReviewsFallback(headers);
        setFallbackMode(true);
        return list;
      }
      throw e;
    }
  }, [getHeaders, fetchReviewsFallback]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const [list, total] = await Promise.all([fetchReviews(), fetchTotalGlobal()]);
      if (cancelRef.current) return;
      setReviews(list);
      setTotalGlobal(total);
      setError(null);
    } catch (e) {
      console.error('[useReviews] refresh failed:', e);
      if (!cancelRef.current) setError(e.message || 'Erreur de chargement');
    } finally {
      if (!cancelRef.current) setLoading(false);
    }
  }, [fetchReviews, fetchTotalGlobal]);

  const sync = useCallback(async () => {
    setSyncing(true);
    try {
      const headers = await getHeaders();
      await fetchJson(`${API_BASE}/api/restaurants/import-reviews`, { method: 'POST', headers });
      const now = Date.now();
      try { localStorage.setItem(LAST_SYNC_KEY, String(now)); } catch (_) {}
      setLastSyncAt(now);
      await refresh();
    } catch (e) {
      console.error('[useReviews] sync failed:', e);
      setError(e.message || 'Synchronisation impossible');
    } finally {
      setSyncing(false);
    }
  }, [getHeaders, refresh]);

  const generateAiReply = useCallback(async (review) => {
    if (!review?.review_id) {
      throw new Error('Suggestion IA indisponible : identifiant manquant.');
    }
    const headers = await getHeaders();
    const body = JSON.stringify({
      review_id: review.review_id,
      author_name: review.author_name,
      review_text: review.text,
      review_rating: review.rating,
    });
    const data = await fetchJson(
      `${API_BASE}/api/dashboard/review-replies/generate`,
      { method: 'POST', headers, body }
    );
    return data.reply_text || '';
  }, [getHeaders]);

  const publishReply = useCallback(async (replyId, replyText) => {
    if (!replyId) throw new Error('reply_id manquant');
    const headers = await getHeaders();
    await fetchJson(
      `${API_BASE}/api/dashboard/review-replies/${replyId}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify({ reply_text: replyText, action: 'publish' }),
      }
    );
    await refresh();
  }, [getHeaders, refresh]);

  const ignoreReply = useCallback(async (replyId) => {
    if (!replyId) throw new Error('reply_id manquant');
    const headers = await getHeaders();
    await fetchJson(
      `${API_BASE}/api/dashboard/review-replies/${replyId}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify({ reply_text: '', action: 'ignore' }),
      }
    );
    await refresh();
  }, [getHeaders, refresh]);

  // Session 8 : vrai endpoint POST /api/reviews/collect.
  // Body { prenom, telephone } ; retourne { success, remaining_quota, plan, unlimited, message_preview }
  // ou throw avec err.code = 'quota_exceeded' / 'invalid_telephone' / 'invalid_prenom' selon l'erreur.
  const sendSmsCollect = useCallback(async (payload) => {
    const headers = await getHeaders();
    try {
      const data = await fetchJson(`${API_BASE}/api/reviews/collect`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload || {}),
      });
      return { success: true, ...data };
    } catch (e) {
      // fetchJson re-throw avec err.payload contenant { error, message }
      const code = e.payload?.error || null;
      const err = new Error(e.payload?.message || e.message || 'Erreur d\'envoi');
      err.code = code;
      err.status = e.status;
      err.payload = e.payload;
      throw err;
    }
  }, [getHeaders]);

  // Mount : lecture lastSyncAt + fetch initial + auto-sync si > 1h
  useEffect(() => {
    cancelRef.current = false;
    let last = null;
    try {
      const v = localStorage.getItem(LAST_SYNC_KEY);
      last = v ? Number(v) : null;
    } catch (_) {}
    if (last) setLastSyncAt(last);

    (async () => {
      const stale = !last || (Date.now() - last) > SYNC_TTL_MS;
      if (stale) {
        // Sync auto en arrière-plan, le fetch initial se déclenche en parallèle
        sync().catch(() => {});
      } else {
        await refresh();
      }
    })();

    return () => { cancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    reviews,
    totalGlobal,
    loading,
    syncing,
    error,
    lastSyncAt,
    fallbackMode,
    refresh,
    sync,
    generateAiReply,
    publishReply,
    ignoreReply,
    sendSmsCollect,
  };
}
